// ══════════════════════════════════════════════════════════
// STREAM URL BUILDER - Helper centralizado
//
// Construye URLs HTTPS seguras para streams de audio y metadata
// usando el proxy oficial de AsuraHosting/CentovaCast.
//
// Formato: https://{hostname}/proxy/{username}/{mountpoint}
// ══════════════════════════════════════════════════════════

// ── Tipos ──

export interface StreamUrlParams {
  loginHostname: string;
  streamUsername: string;
  mountpoint?: string;
  streamingType?: 'shoutcast' | 'icecast';
}

// ── Normalizadores ──

/** Elimina protocolo, barras finales y espacios del hostname. */
function normalizeHostname(hostname: string): string {
  return hostname
    .replace(/^https?:\/\//i, '')
    .replace(/\/+$/, '')
    .replace(/\/+/g, '/')
    .trim();
}

/** Elimina barras del username para evitar URLs malformadas. */
function normalizeUsername(username: string): string {
  return username
    .replace(/\/+/g, '')
    .trim();
}

/** Elimina barras iniciales y finales del mountpoint. */
function normalizeMountpoint(mountpoint: string): string {
  return mountpoint
    .replace(/^\/+/, '')
    .replace(/\/+$/, '')
    .trim();
}

// ── Builder principal ──

/**
 * Construye la URL HTTPS del stream de audio usando el proxy de AsuraHosting.
 *
 * @example
 * buildStreamUrl({
 *   loginHostname: 'cast3.asurahosting.com',
 *   streamUsername: 'radiofrecuenciaweb',
 *   mountpoint: 'stream',
 *   streamingType: 'shoutcast',
 * })
 * // => 'https://cast3.asurahosting.com/proxy/radiofrecuenciaweb/stream'
 */
export function buildStreamUrl(params: StreamUrlParams): string {
  const hostname = normalizeHostname(params.loginHostname);
  const username = normalizeUsername(params.streamUsername);
  const mount = normalizeMountpoint(
    params.mountpoint || (params.streamingType === 'icecast' ? 'live' : 'stream'),
  );

  return `https://${hostname}/proxy/${username}/${mount}`;
}

/**
 * Construye la URL HTTPS para consultar metadata del stream.
 *
 * @param endpoint - 'stats' para JSON con toda la metadata, 'currentsong' solo la canción actual
 *
 * @example
 * buildMetadataUrl({
 *   loginHostname: 'cast3.asurahosting.com',
 *   streamUsername: 'radiofrecuenciaweb',
 * }, 'stats')
 * // => 'https://cast3.asurahosting.com/proxy/radiofrecuenciaweb/stats?json=1'
 */
export function buildMetadataUrl(
  params: Pick<StreamUrlParams, 'loginHostname' | 'streamUsername'>,
  endpoint: 'stats' | 'currentsong',
): string {
  const hostname = normalizeHostname(params.loginHostname);
  const username = normalizeUsername(params.streamUsername);

  if (endpoint === 'stats') {
    return `https://${hostname}/proxy/${username}/stats?json=1`;
  }
  return `https://${hostname}/proxy/${username}/currentsong`;
}